import { domains } from 'lib/config'
import { createClient } from 'lib/Api'
import { AxiosPromise } from 'axios'
import { buildQueryString } from 'lib/api/helpers'
import {
  AssignNetSuiteAssetRequest,
  CacheClearRequest,
  CreateAccessCodeResult,
  CurrentUser,
  DomainProviderRequestParameters,
  ExternalSiteMappingType,
  MergeSiteParameters,
  NetSuiteAsset,
  NetSuiteAssetFilters,
  NetSuiteCustomer,
  NetSuiteCustomerFilters,
  PacingUploadResults,
  PatchResponse,
  ProfileSearchFilters,
  SendEmailRequest,
  Site,
  SiteFilters,
  SiteUpdate,
  SiteUser,
  UpdateUserProfileRequest,
} from 'types'

/**
 * Second Step API Client Architecture:
 *
 * This file demonstrates the pattern for creating API clients that:
 * 1. Use the base API client from lib/Api.js (which handles authentication - shared across all instances that use it)
 * 2. Point to specific API versions (v3, v4, etc.)
 * 3. Provide typed methods for specific endpoints
 * 4. Support custom API addresses for different environments
 *

 * To create a new API version client:
 * 1. Create a new file (e.g., secondstepApiV5.ts)
 * 2. Change the baseApiAddress to point to the desired version
 * 3. Add/remove methods as needed for that version
 */

const baseApiAddress = `${domains.SS_API_V4}`

// We need to set auth on this specific client instance
export const _ssapi = (customApiAddress?: string) => {
  const _api = createClient(customApiAddress || baseApiAddress)

  return {
    // =========================================================================
    // CACHE
    // =========================================================================
    clearCache: (params: CacheClearRequest) =>
      _api.post('/cache/clear', params),

    // =========================================================================
    // DOMAIN PROVIDERS
    // =========================================================================
    postDomainProvider: (parameters: DomainProviderRequestParameters) =>
      _api.post(`/domainprovider`, parameters),

    // =========================================================================
    // EXTERNAL SITE MAPPING
    // =========================================================================
    bulkAddExternalSiteMapping: (
      bodyFormData: FormData,
    ): AxiosPromise<PatchResponse<ExternalSiteMappingType>> =>
      _api.patch(`/sites/externalsitemapping`, bodyFormData),

    // =========================================================================
    // LOCATION
    // =========================================================================
    getCities: (country: string, region?: string): AxiosPromise<string[]> => {
      const params: Record<string, string> = { country }
      if (region) {
        params.region = region
      }
      const queryString = buildQueryString(params)
      return _api.get(`/location/cities?${queryString}`)
    },
    getCountries: (): AxiosPromise<string[]> => _api.get('/location/countries'),
    getRegions: (country: string): AxiosPromise<string[]> =>
      _api.get(`/location/regions?country=${country}`),

    // =========================================================================
    // NETSUITE ASSETS
    // =========================================================================
    getNetSuiteAssetById: (assetId: number): AxiosPromise<NetSuiteAsset> =>
      _api.get(`/netsuiteassets/${assetId}`),
    getNetSuiteAssets: (
      params: NetSuiteAssetFilters,
    ): AxiosPromise<NetSuiteAsset[]> => {
      const queryString = buildQueryString(params)
      return _api.get(`/netsuiteassets?${queryString}`)
    },
    patchNetSuiteAssets: (bodyFormData: FormData) =>
      _api.patch(`/netsuiteassets`, bodyFormData),
    updateNetSuiteAsset: (assetId: number, asset: AssignNetSuiteAssetRequest) =>
      _api.put(`/netsuiteassets/${assetId}`, asset),

    // =========================================================================
    // NETSUITE CUSTOMERS
    // =========================================================================
    getNetSuiteCustomer: (id: number): AxiosPromise<NetSuiteCustomer> =>
      _api.get(`/netsuitecustomers/${id}`),
    getNetSuiteCustomers: (
      params: NetSuiteCustomerFilters,
    ): AxiosPromise<NetSuiteCustomer[]> => {
      const queryString = buildQueryString(params)
      return _api.get(`/netsuitecustomers?${queryString}`)
    },

    // =========================================================================
    // PACING
    // =========================================================================
    patchPacing: (bodyFormData: FormData): AxiosPromise<PacingUploadResults> =>
      _api.patch(`/sites/pacing`, bodyFormData),

    // =========================================================================
    // PROFILE
    // =========================================================================
    addPakToProductUser: (pakNumber: string, userId: number) =>
      _api.post('/userpak', {
        PakNumber: pakNumber,
        UserId: userId,
      }),
    deleteUser: (userEmail: string) =>
      _api.delete(`/profile/deleteUser/${encodeURIComponent(userEmail)}`),
    getArchetypes: () => _api.get('/profile/archetypes'),
    getSetupAdminUserId: (setupAdminEmail: string) =>
      _api.get(`/profile/search?email=${setupAdminEmail}`),
    getUserDetails: (userId: number) =>
      _api.get(`/profile/details?userId=${userId}`),
    getUserProfile: (): AxiosPromise<CurrentUser> => _api.get('/profile/me'),
    removePakFromProductUser: (userEmail: string, pakNo: string) =>
      _api.delete(
        `/admin/user/${encodeURIComponent(userEmail)}/pak/${encodeURIComponent(
          pakNo,
        )}`,
      ),
    saveProductUser: (
      productUser: UpdateUserProfileRequest,
    ): AxiosPromise<CurrentUser> => _api.put('/profile', productUser),
    searchProfiles: (searchCriteria: ProfileSearchFilters) => {
      const queryString = buildQueryString(searchCriteria)
      return _api.get(`/profile/search?${queryString}`)
    },
    updateUserProfile: (
      profile: UpdateUserProfileRequest,
    ): AxiosPromise<void> => _api.put('/profile/me', profile),
    uploadPakFile: (bodyFormData: FormData): AxiosPromise<any> =>
      _api.post('/pak', bodyFormData),

    // =========================================================================
    // SITES
    // =========================================================================
    bulkUpdateSitePreferences: (bodyFormData: FormData) =>
      _api.patch(`/sites/bulkpreferences`, bodyFormData),
    createAccessCodes: (
      siteIds: number[],
    ): AxiosPromise<PatchResponse<CreateAccessCodeResult>> =>
      _api.patch(`/sites/accessCodes`, { siteIds }),
    getSite: (siteId: number): AxiosPromise<Site> =>
      _api.get(`/sites/${siteId}`),
    getSiteAccessCodes: (siteId: number): AxiosPromise<string[]> =>
      _api.get(`/sites/${siteId}/accesscodes`),
    getSites: (params: SiteFilters): AxiosPromise<Site[]> => {
      const queryString = buildQueryString(params)
      return _api.get(`/sites?${queryString}`)
    },
    mergeSite: (params: MergeSiteParameters) => {
      const { siteId, primarySiteId, isPreview } = params
      return _api.put(`/sites/${siteId}/move`, {
        primarySiteId,
        isPreview,
      })
    },
    siteOrgNetSuiteCustomerSearch: (nsCustomerId: string, orgId: number) =>
      _api.get(`/sites?NSCustomerId=${nsCustomerId}&OrganizationId=${orgId}`),
    updateSite: (site: SiteUpdate) => _api.put(`/sites/${site.id}`, site),

    // =========================================================================
    // SITE USERS
    // =========================================================================
    bulkCreateSiteUsers: (
      siteId: number,
      siteUsers: Partial<SiteUser>[],
      sendEmail: boolean = true,
    ) =>
      _api.patch(
        `/sites/${siteId}/siteUsers?sendEmail=${sendEmail}`,
        siteUsers,
      ),
    createSiteUser: (siteUser: Partial<SiteUser>, sendEmail: boolean = true) =>
      _api.post(
        `/sites/${siteUser.siteId}/siteUsers?sendEmail=${sendEmail}`,
        siteUser,
      ),
    getSiteUsers: (siteId: number) => _api.get(`/sites/${siteId}/siteUsers`),
    remindSiteUser: (siteUser: SiteUser) =>
      _api.post(`/sites/${siteUser.siteId}/siteusers/${siteUser.id}/remind`),
    remindSiteUsers: (siteId: number, emailFilters: SendEmailRequest) =>
      _api.post(`/sites/${siteId}/siteusers/remind`, emailFilters),
    updateSiteUser: (siteUser: SiteUser) =>
      _api.put(`/sites/${siteUser.siteId}/siteUsers/${siteUser.id}`, siteUser),
  }
}
const ssapi = _ssapi()
export default ssapi
